import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Typography,
  Box,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CalendarIcon from "@mui/icons-material/CalendarToday";
import PhoneIcon from "@mui/icons-material/Phone";
import { useTranslation } from "react-i18next";
import { triggerOrderRefresh } from "../../hooks/useOrderNotifications";
import api from "../../services/ApiService";

interface Performer {
  id: number;
  name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  average_rating?: number;
  completed_orders?: number;
  city?: string;
  date_of_birth?: string;
  bio?: string;
  is_verified?: boolean;
}

interface OrderResponse {
  id: number;
  created_at: string;
  updated_at: string;
  order_id: number;
  performer_id: number;
  message?: string;
  bid?: number;
  status: "active" | "selected" | "rejected";
  performer: Performer;
}

interface OrderResponsesModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  onPerformerSelected?: (performerId: number) => void;
  onPerformerRejected?: (performerId: number, reason?: string) => void;
  isClient: boolean;
  showContacts?: boolean;
}

const OrderResponsesModal: React.FC<OrderResponsesModalProps> = ({
  open,
  onClose,
  orderId,
  onPerformerSelected,
  onPerformerRejected,
  isClient,
  showContacts = false,
}) => {
  const { t } = useTranslation();
  const [responses, setResponses] = useState<OrderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerformer, setSelectedPerformer] = useState<Performer | null>(
    null,
  );
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadResponses();
    }
  }, [open, orderId]);

  const loadResponses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api
        .getAxiosInstance()
        .get(`/orders/${orderId}/responses`);
      setResponses(response.data.data || []);
    } catch (err: any) {
      console.error("Failed to load order responses:", err);
      setError(err.response?.data?.error || t("error_loading_responses"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfile = (performer: Performer) => {
    setSelectedPerformer(performer);
    setShowProfileDialog(true);
  };

  const handleSelectPerformer = async (performerId: number) => {
    try {
      await api.getAxiosInstance().put(`/orders/${orderId}/assign`, {
        performer_id: performerId,
      });

      // Trigger global order refresh to update ResponsesPanel and other components
      triggerOrderRefresh();

      if (onPerformerSelected) {
        onPerformerSelected(performerId);
      }

      loadResponses(); // Refresh list
    } catch (err: any) {
      console.error("Failed to assign performer:", err);
      alert(err.response?.data?.error || t("error_assigning_performer"));
    }
  };

  const handleRejectPerformer = async (
    performerId: number,
    reason?: string,
  ) => {
    setRejectingId(performerId);
    try {
      await api.getAxiosInstance().put(`/orders/${orderId}/reject-performer`, {
        performer_id: performerId,
        reason: reason,
      });

      if (onPerformerRejected) {
        onPerformerRejected(performerId, reason);
      }

      loadResponses(); // Refresh list
    } catch (err: any) {
      console.error("Failed to reject performer:", err);
      alert(err.response?.data?.error || t("error_rejecting_performer"));
    } finally {
      setRejectingId(null);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      active: { label: t("active"), color: "success" as const },
      selected: { label: t("selected"), color: "primary" as const },
      rejected: { label: t("rejected"), color: "error" as const },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const activeResponses = responses.filter((r) => r.status === "active");
  const selectedResponse = responses.find((r) => r.status === "selected");
  const rejectedResponses = responses.filter((r) => r.status === "rejected");

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {t("order_responses")}
          <Typography variant="body2" color="text.secondary">
            {activeResponses.length} {t("active_responses")}
            {selectedResponse && ` • ${t("one_selected")}`}
            {rejectedResponses.length > 0 &&
              ` • ${rejectedResponses.length} ${t("rejected")}`}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : responses.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <PersonIcon
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                {t("no_responses_yet")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("no_performers_responded")}
              </Typography>
            </Box>
          ) : (
            <List>
              {responses.map((response, index) => (
                <React.Fragment key={response.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title={t("view_profile")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleViewProfile(response.performer)
                            }
                          >
                             <RemoveRedEyeIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isClient && response.status === "active" ? t("select_performer") : t("cant_select_performer")}>
                          <span> {/* Wrapper span for tooltip when button is disabled */}
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() =>
                                handleSelectPerformer(response.performer_id)
                              }
                              disabled={!isClient || response.status !== "active"}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isClient && response.status === "active" ? t("reject_performer") : t("cant_reject_performer")}>
                          <span> {/* Wrapper span for tooltip when button is disabled */}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleRejectPerformer(response.performer_id)
                              }
                              disabled={!isClient || response.status !== "active" || rejectingId === response.performer_id}
                            >
                              <CancelIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={response.performer.avatar_url}
                        alt={response.performer.name}
                        onClick={() => handleViewProfile(response.performer)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography 
                            variant="subtitle1" 
                            component="span"
                            onClick={() => handleViewProfile(response.performer)}
                            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                          >
                            {response.performer.name}
                          </Typography>
                          {getStatusChip(response.status)}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <StarIcon
                                sx={{ fontSize: 16, color: "warning.main" }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {response.performer.average_rating?.toFixed(
                                  1,
                                ) || "0.0"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <WorkIcon
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {response.performer.completed_orders || 0}{" "}
                                {t("completed_orders_short")}
                              </Typography>
                            </Box>
                          </Box>
                          {response.message && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              {response.message}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {t("responded_at")}:{" "}
                            {new Date(response.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{t("close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog
        open={showProfileDialog}
        onClose={() => setShowProfileDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedPerformer && (
          <>
            <DialogTitle>{t("performer_profile")}</DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <Box sx={{ position: "relative", mb: 2 }}>
                  <Avatar
                    src={selectedPerformer.avatar_url}
                    sx={{ width: 120, height: 120 }}
                  >
                    <PersonIcon />
                  </Avatar>
                  {selectedPerformer.is_verified && (
                    <Tooltip title={t("verified_performer")}>
                      <CheckCircleIcon
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          width: 28,
                          height: 28,
                          color: "success.main",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="h5" gutterBottom fontWeight="600">
                  {selectedPerformer.name}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Chip
                    icon={<StarIcon />}
                    label={`${selectedPerformer.average_rating?.toFixed(2) || "0.0"}`}
                    color="warning"
                    size="small"
                  />
                  <Chip
                    icon={<WorkIcon />}
                    label={`${selectedPerformer.completed_orders || 0}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ width: "100%", px: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  {t("basic_info")}
                </Typography>

                {selectedPerformer.city && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <LocationOnIcon
                      sx={{ fontSize: 20, color: "text.secondary" }}
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("city")}
                      </Typography>
                      <Typography variant="body2">
                        {selectedPerformer.city}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {selectedPerformer.date_of_birth && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <CalendarIcon
                      sx={{ fontSize: 20, color: "text.secondary" }}
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("date_of_birth")}
                      </Typography>
                      <Typography variant="body2">
                        {new Date(
                          selectedPerformer.date_of_birth,
                        ).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {selectedPerformer.bio && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("about")}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {selectedPerformer.bio}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Контакты - только после подтверждения */}
              {showContacts ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ width: "100%", px: 2, mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      color="success.main"
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
                      />
                      {t("contacts_confirmed")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <PhoneIcon
                        sx={{ fontSize: 20, color: "text.secondary" }}
                      />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("phone")}
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {selectedPerformer.phone || t("not_specified")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <PersonIcon
                        sx={{ fontSize: 20, color: "text.secondary" }}
                      />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t("email")}
                        </Typography>
                        <Typography variant="body2">
                          {selectedPerformer.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box sx={{ width: "100%", px: 2, mt: 2 }}>
                  <Alert
                    severity="info"
                    icon={<VisibilityIcon />}
                    sx={{ fontSize: 12 }}
                  >
                    <Typography variant="caption">
                      {t("contacts_hidden_info")}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowProfileDialog(false)}>
                {t("close")}
              </Button>
              {isClient && !selectedPerformer.phone && (
                <Button
                  onClick={() => {
                    handleSelectPerformer(selectedPerformer.id);
                    setShowProfileDialog(false);
                  }}
                  color="success"
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                >
                  {t("confirm_performer_and_show_contacts")}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default OrderResponsesModal;
